<template>
	<v-card class="comment-form-container elevation-0">
		<v-form ref="form" v-model="valid" lazy-validation @submit.prevent="validateAndSubmit">
			<div class="comment-form__content d-flex justify-space-between align-center">
				<router-link :to="{ name: 'profile' }" class="comment-form__avatar-link">
					<Avatar
						class="comment-form__avatar-form mr-2"
						:size="40"
						:avatar="this.$props.avatar"
					/>
				</router-link>

				<div class="comment-form__input">
					<v-textarea
						class="comment-form"
						label="Write a comment ..."
						auto-grow
						rows="1"
						:value="fields.content"
						v-model="fields.content"
						:error-messages="field_errors.content"
						:rules="rules.content"
						required
					></v-textarea>
				</div>
				<button
					@click="validateAndSubmit"
					class="comment-button"
					type="button"
					:style="{
						backgroundColor: fields.content === '' ? '#9b9b9b' : 'var(--accent-color)',
					}"
				>
					<v-progress-circular v-if="loading" color="#FF5733"></v-progress-circular>
					<span v-else>Send</span>
				</button>
			</div>
			<p v-for="(error, index) in errors" :key="index" class="red--text">
				{{ error }}
			</p>
		</v-form>
		<CommentMentionBar
			v-if="isMentionActive"
			:searchString="fields.content"
			:mentions="mentions"
			@setMention="setMention"
		/>
	</v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Form from '@/mixins/Form'
import { ruleRequired } from '@/utils/rules'
import Avatar from '@/components/Avatar'
import CommentMentionBar from '@/components/CommentMentionBar.vue'
import Dwh from '@/mixins/Dwh'

export default {
	name: 'CommentForm',
	components: {
		Avatar,
		CommentMentionBar,
	},
	mixins: [Form, Dwh],
	props: {
		isReply: {
			type: Boolean,
			default: false,
		},
		parent: {
			type: Object,
			default: () => ({}),
		},
		isEdit: {
			type: Boolean,
			default: false,
		},
		comment: {
			type: Object,
			default: () => ({}),
		},
		avatar: {
			type: String,
			default: '',
		},
	},
	data: () => ({
		fields: {
			content: '',
		},
		mentions: [],
	}),
	methods: {
		...mapActions('post', { postAddComment: 'addComment' }),
		...mapActions('forum', { forumAddComment: 'addComment' }),

		isCurrentUser(userId) {
			if (!this.profile) {
				return false
			}

			const { user_id: currentUserId } = this.profile
			return userId === currentUserId
		},
		submitForm() {
			this.field_errors = {}
			let result = this.fields.content
			this.sendTrigger({
				category: 'Content',
				action: 'click_send_reply_forum_post',
				module: 'Forums',
				chapter_id: this.$route.params.id,
			})
			this.mentions.forEach((item) => {
				const startIndex = result.indexOf(item.visible_username.replace(' ', '')) - 1
				if (startIndex >= 0) {
					const endIndex = item.visible_username.replace(' ', '').length + startIndex + 1
					result =
						result.substring(0, startIndex) +
						`@[${item.user_id}|${item.visible_username}]` +
						result.substring(endIndex)
				}
			})

			this.mentions = []
			if (this.parent.thread_id) {
				return this.postAddComment({
					commentBody: {
						thread_id: this.parent.thread_id || this.parent.forum_id,
						reply_text: result,
					},
					thread_title: this.parent.thread_title,
				})
			}

			if (this.parent.forum_id) {
				return this.forumAddComment({
					thread_id: this.parent.thread_id || this.parent.forum_id,
					reply_text: result,
				})
			}

			return this.forumAddComment({
				thread_id: this.parent.thread_id || this.parent.forum_id,
				reply_text: result,
			})
		},
		setMention(user, number) {
			const splittedContent = this.fields.content.split('@')
			splittedContent[number + 1] = user.visible_username.replace(' ', '')
			this.mentions[number] = user
			this.fields.content = splittedContent.join('@')
		},
	},
	computed: {
		...mapGetters('auth', ['isAuthenticated', 'profile']),
		...mapGetters('post', ['isCommentAdding']),
		...mapGetters('forum', ['isForumCommentAdding']),

		rules() {
			return {
				content: [
					(v) => ruleRequired(v) || 'Field is required.',
					(v) => (typeof v === 'string' && v.trim().length > 0) || 'Text cannot be empty',
				],
			}
		},
		isMentionActive() {
			return (
				this.fields.content &&
				!this.isCommentAdding &&
				!this.isForumCommentAdding &&
				this.fields.content.split('@').length > 1 + this.mentions.length
			)
		},
	},
	watch: {
		'fields.content': {
			handler() {
				if (this.fields.content) {
					const splittedContent = this.fields.content.split('@')
					splittedContent.forEach((item, index) => {
						if (
							index > 0 &&
							this.mentions[index - 1] &&
							!item.startsWith(
								this.mentions[index - 1].visible_username.replace(' ', '')
							)
						) {
							this.mentions.splice(index - 1, 1)
							if (splittedContent[index].split(' ')[1]) {
								splittedContent[index - 1] =
									splittedContent[index - 1] +
									splittedContent[index].split(' ')[1]
							}
							splittedContent.splice(index, 1)
							this.fields.content = splittedContent.join('@')
						}
					})
				}
			},
		},
	},
}
</script>

<style scoped lang="scss">
.comment-form__button {
	width: 70px;
	height: 36px;
	padding: 9px 16px 10px 16px;
	border-radius: 8px;
	margin-right: 13px;
}

.comment-button {
	max-width: 134px;
	width: 100%;
	height: 46px;
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: #002855;
	color: white;
	font-family: 'Inter', sans-serif;
	font-weight: 700;
	font-size: 18px;
	line-height: 100%;
	white-space: nowrap;
}

.comment-form__text-outer {
	width: 555px;
	height: 72px;
	justify-content: space-between;
	padding: 16px;
}

.comment-form-container {
	padding: 8px 12px;
	border-bottom: 1.5px solid #e2e2e2 !important;
}
.comment-form {
	display: flex;
	align-items: center;
	min-height: 48px;
	max-width: 332px;
	font-family: Inter, sans-serif;
	position: relative;
	font-style: normal;
	font-weight: 500;
	font-size: 20px;
	line-height: 24px;
	letter-spacing: 0.5px;
	margin: 0;

	::v-deep(.v-label) {
		font-family: Inter, sans-serif;
		font-weight: 400;
		font-size: 16px;
		line-height: 100%;
		letter-spacing: 0;
		text-align: center;
	}

	&__avatar-form {
		border-radius: 8px;
	}

	&__input {
		width: 100%;
		display: flex;
		align-items: center;
	}

	&__textarea {
		color: #4a4a4a;
		background-color: transparent;
		box-shadow: none;
		@media screen and (max-width: 400px) {
			&__textarea {
				width: 100%;
			}
		}

		&:focus {
			.v-input__slot {
				border: 1px solid #000000;
				margin-bottom: 0 !important;
			}
		}
	}

	&__button {
		background-color: var(--accent-color) !important;
		color: white !important;
		border: none;
		width: 70px;
		height: 36px;
		border-radius: 8px !important;
	}

	@media screen and (max-width: 400px) {
		&__text-outer {
			flex-direction: column;
		}
	}
}

.custom-textarea .v-text-field--is-disabled .v-label {
	display: none;
}
</style>
